/**
 * Shared helpers used by ProjectCard (tile) and ProjectTable (list) so
 * monogram / status-pill / alert-text logic stays in lockstep across the
 * two views.
 */

/**
 * 2-char project monogram. Prefers the alpha prefix of project_number
 * (e.g. "CHICO-D-02" → "CH"); falls back to initials of project_name.
 */
export function getInitials(project) {
  const codePrefix = (project.project_number || '').split('-')[0];
  if (codePrefix.length >= 2) {
    return codePrefix.slice(0, 2).toUpperCase();
  }
  const words = (project.project_name || '?')
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Maps legacy status strings → StatusPill's 3-state model + a display
 * label. Unknown statuses fall through to draft styling with the raw
 * label so we never silently lose information.
 *
 * Mapping rationale:
 *   Active   → active  (live, success-toned)
 *   Planning → draft   (pre-construction, neutral)
 *   On Hold  → risk    (warning amber — needs attention)
 *   Complete → active  (with custom label, still success-toned)
 *   Closed   → draft   (with custom label, neutral)
 */
export function statusToPill(status) {
  switch (status) {
    case 'Active':
      return { status: 'active', label: 'Active' };
    case 'Planning':
      return { status: 'draft', label: 'Planning' };
    case 'On Hold':
      return { status: 'risk', label: 'On hold' };
    case 'Complete':
      return { status: 'active', label: 'Complete' };
    case 'Closed':
      return { status: 'draft', label: 'Closed' };
    default:
      return { status: 'draft', label: status || 'Active' };
  }
}

/**
 * Compact alert-chip text generator. Used for both project alerts
 * (tasks behind schedule) and dependency alerts (scheduling issues).
 *
 *   alertText(2, 1, 'task', 'behind schedule')
 *     → "2 tasks behind schedule (1 critical)"
 *
 *   alertText(3, 0, 'scheduling issue', '')
 *     → "3 scheduling issues"
 */
export function alertText(count, critical, noun, suffix) {
  const base = `${count} ${noun}${count === 1 ? '' : 's'}`;
  const tail = suffix ? ` ${suffix}` : '';
  const crit = critical > 0 ? ` (${critical} critical)` : '';
  return base + tail + crit;
}

/**
 * Convenience: PM column label is "Managers" if comma-separated,
 * otherwise "PM". Used by the tile detail grid.
 */
export function managerLabel(value) {
  return value && value.includes(',') ? 'Managers' : 'PM';
}

/**
 * Billed to date and retainage as the dashboard shows them: Summit pay apps or
 * CE's invoices, whichever is further along (#63; server/ceBilling.js). Falls
 * back to the Summit figures for a row without `billing`.
 */
export function billedOf(project) {
  return project.billing ? Number(project.billing.billed) || 0 : parseFloat(project.total_billed) || 0;
}

export function retainageOf(project) {
  return project.billing ? Number(project.billing.retainage) || 0 : parseFloat(project.total_retainage) || 0;
}

/**
 * Kebab-menu actions after "View details" (#18 job archive, #71). ProjectList passes
 * these handlers only to people with the Archive & Restore Jobs permission, so
 * everyone else sees just "View details". An active job offers Archive; an
 * archived one offers Restore -- here, the Dashboard's Archived tab, and nowhere
 * else (Duncan 2026-10-19: "It should not be an easy function to restore"). Nothing
 * is ever deleted: "We don't wanna delete them ever".
 */
export function jobActionItems(project, { onArchive, onRestore } = {}) {
  const items = project.archived_at
    ? [onRestore && { label: 'Restore', onClick: () => onRestore(project) }]
    : [onArchive && { label: 'Archive job', onClick: () => onArchive(project) }];
  const actions = items.filter(Boolean);
  return actions.length ? [{ divider: true }, ...actions] : [];
}

/**
 * The archive's year headers (#71), on the Dashboard's Archived tab and Job Costs →
 * Archived: the year of the job's CONTRACT date (Duncan 2026-10-19: "the archived date
 * is the contract date not the finish date"), newest first; a job with no contract
 * date (or no Summit project) goes under NO_CONTRACT_YEAR, last.
 */
export const NO_CONTRACT_YEAR = 'No contract date';

export function contractYearOf(project) {
  const m = /^(\d{4})-\d{2}-\d{2}/.exec(String((project && project.contract_date) || ''));
  return m ? m[1] : NO_CONTRACT_YEAR;
}

/** [{ key: '2026', rows }, …, { key: NO_CONTRACT_YEAR, rows }] -- each group keeps `items`' order. */
export function groupByContractYear(items, projectOf) {
  const groups = new Map();
  items.forEach((item) => {
    const key = contractYearOf(projectOf(item));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === NO_CONTRACT_YEAR) - (b === NO_CONTRACT_YEAR) || Number(b) - Number(a))
    .map(([key, rows]) => ({ key, rows }));
}
